import React, { useState, useEffect } from 'react';
import { usePacientes } from '../../hooks/usePacientes';
import { toast } from 'react-hot-toast';
import { useSeguros } from '../../hooks/useSeguros';

interface Paciente {
  _id: string;
  nombre: string;
  documento: string;
  telefono: string;
  email: string;
  fechaNacimiento: Date;
  direccion: string;
  seguro: string | { _id: string; nombre?: string } | null;
  seguroNombre?: string;
  codigo?: string;
  menorEdad?: boolean;
  procedencia: string;
  genero: string;
  nacionalidad: string;
  noAfiliado: string;
  medico?: string;
}

interface PacienteFormProps {
  onClose: () => void;
  onSuccess: () => void;
  pacienteToEdit?: Paciente | null;
  isEditing?: boolean;
}

const PacienteForm: React.FC<PacienteFormProps> = ({
  onClose,
  onSuccess,
  pacienteToEdit,
  isEditing = false
}) => {
  const { addPaciente, editPaciente } = usePacientes();
  const { seguros } = useSeguros();
  const [formData, setFormData] = useState({
    nombre: '',
    apellido: '',
    documento: '',
    telefono: '',
    email: '',
    fechaNacimiento: '',
    direccion: '',
    seguro: '',
    seguroNombre: '',
    procedencia: '',
    genero: '',
    noAfiliado: '',
    medico: '',
    nacionalidad: 'Dominicana',
  });

  const [menorEdad, setMenorEdad] = useState(false);
  const [noDocumento, setNoDocumento] = useState(false);

  useEffect(() => {
    if (isEditing && pacienteToEdit) {
      const fechaNacimiento = new Date(pacienteToEdit.fechaNacimiento)
        .toISOString()
        .split('T')[0];

      // Divide el nombre completo en la última palabra para el apellido
      const nombreCompleto = pacienteToEdit.nombre || '';
      const palabras = nombreCompleto.split(' ');
      const apellido = palabras.pop() || ''; // Toma la última palabra como apellido
      const nombre = palabras.join(' '); // El resto es el nombre

      // El seguro puede venir como ObjectId string, objeto poblado ({ _id, nombre }) o null
      const seguroObj =
        typeof pacienteToEdit.seguro === 'object' && pacienteToEdit.seguro
          ? pacienteToEdit.seguro
          : null;
      const seguroId = (
        typeof pacienteToEdit.seguro === 'object' && pacienteToEdit.seguro
          ? pacienteToEdit.seguro._id
          : pacienteToEdit.seguro
      ) || '';

      setMenorEdad(pacienteToEdit.menorEdad || false);

      setFormData({
        ...pacienteToEdit,
        nombre: nombre,
        apellido: apellido,
        fechaNacimiento,
        seguro: seguroId,
        seguroNombre: seguroObj?.nombre || pacienteToEdit.seguroNombre || '',
        medico: pacienteToEdit.medico || '',
      });
    }
  }, [isEditing, pacienteToEdit]);

  // Formatea el documento a xxx-xxxxxxx-x
  const formatDocumento = (value: string) => {
    // Elimina todo lo que no sea número
    const digits = value.replace(/\D/g, '');
    // Aplica el formato
    let formatted = digits;
    if (digits.length > 3 && digits.length <= 10) {
      formatted = `${digits.slice(0, 3)}-${digits.slice(3, 10)}`;
    }
    if (digits.length > 10) {
      formatted = `${digits.slice(0, 3)}-${digits.slice(3, 10)}-${digits.slice(10, 11)}`;
    }
    return formatted;
  };

  // Formatea el teléfono a xxx-xxx-xxxx (ajusta si necesitas otro formato)
  const formatTelefono = (value: string) => {
    const digits = value.replace(/\D/g, '');
    let formatted = digits;
    if (digits.length > 3 && digits.length <= 6) {
      formatted = `${digits.slice(0, 3)}-${digits.slice(3, 6)}`;
    }
    if (digits.length > 6) {
      formatted = `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6, 10)}`;
    }
    return formatted;
  };

  // Modifica handleChange para formatear documento y teléfono
  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    if (name === 'menorEdad') {
      const isChecked = (e.target as HTMLInputElement).checked;
      setMenorEdad(isChecked);
      if (isChecked) {
        setFormData((prev) => ({ ...prev, documento: '' }));
      }
      return;
    }
    if (name === 'noDocumento') {
      const isChecked = (e.target as HTMLInputElement).checked;
      setNoDocumento(isChecked);
      if (isChecked) {
        setFormData((prev) => ({ ...prev, documento: '' }));
      }
      return;
    }
    let newValue = value;
    if (name === 'documento') {
      newValue = formatDocumento(value);
    }
    if (name === 'telefono') {
      newValue = formatTelefono(value);
    }
    setFormData((prev) => ({ ...prev, [name]: newValue }));
  };

  // Modifica la función handleNameChange
  const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => {
      if (name === 'nombre' || name === 'apellido') {
        const nombreValue = name === 'nombre' ? value : prev.nombre;
        const apellidoValue = name === 'apellido' ? value : prev.apellido;
        return {
          ...prev,
          nombre: `${nombreValue}`,
          apellido: `${apellidoValue}`,
        };
      }
      return { ...prev, [name]: value };
    });
  };

  const validarFechaNacimiento = (fecha: string): boolean => {
    if (!fecha) return false;

    const fechaNacimiento = new Date(fecha);
    const hoy = new Date();

    // Verificar que la fecha sea válida
    if (isNaN(fechaNacimiento.getTime())) {
      toast.error('Por favor ingresa una fecha de nacimiento válida');
      return false;
    }

    // Verificar que la fecha no sea futura
    if (fechaNacimiento > hoy) {
      toast.error('La fecha de nacimiento no puede ser futura');
      return false;
    }

    // Calcular la edad en años
    let edad = hoy.getFullYear() - fechaNacimiento.getFullYear();
    const mes = hoy.getMonth() - fechaNacimiento.getMonth();

    if (mes < 0 || (mes === 0 && hoy.getDate() < fechaNacimiento.getDate())) {
      edad--;
    }

    // Verificar que no exceda los 200 años
    if (edad > 200) {
      toast.error('La fecha de nacimiento no puede ser mayor a 200 años');
      return false;
    }

    // Verificar que sea al menos 0 años (nacido hoy)
    if (edad < 0) {
      toast.error('La fecha de nacimiento no puede ser futura');
      return false;
    }

    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.nombre || !formData.fechaNacimiento || !formData.telefono) {
      toast.error('Por favor completa los campos obligatorios');
      return;
    }
    let documentoFinal = formData.documento;
    let codigoFinal = formData.documento;
    const isCreating = !(isEditing && pacienteToEdit);

    // Only derive the codigo from the documento at creation. On edit the
    // codigo is immutable: the minor/documento regeneration must never run.
    if (isCreating && (menorEdad || (!formData.documento && noDocumento))) {
      const now = new Date();
      const pad = (n: number) => n.toString().padStart(2, '0');
      const year = pad(now.getFullYear() % 100);
      const fechaNum = `${year}${pad(now.getMonth() + 1)}${pad(now.getDate())}${pad(now.getHours())}${pad(now.getMinutes())}${pad(now.getSeconds())}`;
      documentoFinal = menorEdad ? `M-${fechaNum}` : fechaNum;
      codigoFinal = documentoFinal;
    } else if (isCreating && !menorEdad && !formData.documento) {
      toast.error('El documento es obligatorio para mayores de edad');
      return;
    }

    if (!formData.genero) {
      toast.error('Por favor selecciona el género');
      return;
    }

    if (!formData.seguro || formData.seguro === 'ninguno') {
      toast.error('Por favor selecciona un seguro médico');
      return;
    }

    if (!formData.procedencia) {
      toast.error('Por favor selecciona la procedencia');
      return;
    }

    if (!validarFechaNacimiento(formData.fechaNacimiento)) {
      return;
    }

    if (formData.email && !/\S+@\S+\.\S+/.test(formData.email)) {
      toast.error('Por favor ingresa un email válido');
      return;
    }

    if (formData.telefono && !/^\d{3}-\d{3}-\d{4}$/.test(formData.telefono)) {
      toast.error('El teléfono debe tener el formato 809-123-4567');
      return;
    }

    try {
      const nombreCompleto = `${formData.nombre} ${formData.apellido}`.trim();
      const selectedSeguro = seguros.find(s => s._id === formData.seguro);
      const pacienteData = {
        ...formData,
        nombre: nombreCompleto,
        documento: documentoFinal,
        fechaNacimiento: new Date(formData.fechaNacimiento),
        seguroNombre: selectedSeguro?.nombre || '',
        menorEdad,
      };

      if (isEditing && pacienteToEdit) {
        // codigo is immutable on edit: omit it from the payload (undefined is
        // dropped by JSON serialization) so the identity is never rewritten.
        const editData = { ...pacienteData, codigo: undefined };
        await editPaciente(pacienteToEdit.codigo || '', editData);
      } else {
        const createData = { ...pacienteData, codigo: codigoFinal };
        await addPaciente(createData);
      }
      await onSuccess();

    } catch (error: any) {
      toast.error(error.message || (isEditing
        ? 'Error al actualizar el paciente'
        : 'Error al agregar el paciente'
      ));
    }
  };

  return (
    <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-2xl mx-auto">
      <form onSubmit={handleSubmit} className="space-y-6">
        <h2 className="text-2xl font-semibold text-gray-800 mb-4 border-b pb-3">
          {isEditing ? 'Editar Paciente' : 'Nuevo Paciente'}
        </h2>

        {/* Checkbox menor de edad */}
        <div className="mb-4">
          <label className="inline-flex items-center">
            <input
              type="checkbox"
              name="menorEdad"
              checked={menorEdad}
              onChange={handleChange}
              className="form-checkbox h-5 w-5 text-blue-600"
            />
            <span className="ml-2 text-sm text-gray-700">Menor de edad (no requiere documento)</span>
          </label>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Nombre */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Nombres <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              name="nombre"
              value={formData.nombre}
              onChange={handleNameChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              required
            />
          </div>

          {/* Apellido */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Apellidos <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              name="apellido"
              value={formData.apellido}
              onChange={handleNameChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              required
            />
          </div>

          {/* Documento */}
          {!menorEdad && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Documento <span className="text-red-500">*</span>
                <label className="inline-flex items-center ml-4">
                  <input
                    type="checkbox"
                    name="noDocumento"
                    checked={noDocumento}
                    onChange={handleChange}
                    className="form-checkbox h-4 w-4 text-blue-600"
                  />
                  <span className="ml-2 text-sm text-gray-700">No documento</span>
                </label>
              </label>
              {!noDocumento && (
                <input
                  type="text"
                  name="documento"
                  value={formData.documento}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                />
              )}
            </div>
          )}

          {/* Teléfono */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Teléfono <span className="text-red-500">*</span></label>
            <input
              type="tel"
              name="telefono"
              value={formData.telefono}
              onChange={handleChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              required
            />
          </div>

          {/* Email */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Email</label>
            <input
              type="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          {/* Nacionalidad */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Nacionalidad <span className="text-red-500">*</span></label>
            <select
              name="nacionalidad"
              value={formData.nacionalidad}
              onChange={handleChange}
              required
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="Dominicana">Dominicana</option>
              <option value="Haitiana">Haitiana</option>
              <option value="otra">otra</option>
            </select>
          </div>

          {/* Fecha de Nacimiento */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Fecha de Nacimiento <span className="text-red-500">*</span>
            </label>
            <input
              type="date"
              name="fechaNacimiento"
              value={formData.fechaNacimiento}
              onChange={handleChange}
              min={new Date(new Date().getFullYear() - 200, 0, 1).toISOString().split('T')[0]}
              max={new Date().toISOString().split('T')[0]}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              required
            />
            <p className="text-xs text-gray-500 mt-1">
              La fecha no puede ser mayor a 200 años ni futura
            </p>
          </div>

          {/* No. Afiliado */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">No. Afiliado</label>
            <input
              type="text"
              name="noAfiliado"
              value={formData.noAfiliado}
              onChange={handleChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          {/* Seguro Médico */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Seguro Médico <span className="text-red-500">*</span>
            </label>
            <select
              name="seguro"
              value={formData.seguro}
              onChange={handleChange}
              required
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="ninguno">Seleccione</option>
              {seguros
                .filter(seguro => seguro.activo)
                .map(seguro => (
                  <option key={seguro._id} value={seguro._id}>
                    {seguro.nombre}
                  </option>
                ))}
            </select>
          </div>

          {/* Género */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Género <span className="text-red-500">*</span></label>
            <select
              name="genero"
              value={formData.genero}
              onChange={handleChange}
              required
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">Seleccione género</option>
              <option value="masculino">Masculino</option>
              <option value="femenino">Femenino</option>
            </select>
          </div>

          {/* Medico*/}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Medico</label>
            <input
              type="text"
              name="medico"
              value={formData.medico}
              onChange={handleChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          {/* Procedencia */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Procedencia <span className="text-red-500">*</span></label>
            <select
              name="procedencia"
              value={formData.procedencia}
              onChange={handleChange}
              required
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">Seleccione Procedencia</option>
              <option value="Hospitalizado">Hospitalizado</option>
              <option value="Ambulatorio">Ambulatorio</option>
              <option value="Emergencia">Emergencia</option>
            </select>
          </div>
        </div>

        {/* Dirección */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Dirección</label>
          <textarea
            name="direccion"
            value={formData.direccion}
            onChange={handleChange}
            rows={3}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>

        {/* Action Buttons */}
        <div className="flex justify-end space-x-4 pt-4 border-t">
          <button
            type="button"
            onClick={onClose}
            className="px-6 py-2 text-gray-600 hover:bg-gray-100 rounded-md transition-colors"
          >
            Cancelar
          </button>
          <button
            type="submit"
            className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
          >
            {isEditing ? 'Actualizar' : 'Guardar'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default PacienteForm;